package co.maytes.standupbot.dashboard.features.standupruns.tabs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.EmptyState
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideList
import co.maytes.standupbot.dashboard.features.settings.RetentionCopy
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standuprun.StandupRunDetailDto
import co.maytes.standupbot.shared.util.RelativeTime
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.dom.A

@Composable
internal fun RawActivityTab(detail: StandupRunDetailDto, retentionDays: Int) {
    val linearGrouped = remember(detail.rawLinearActivity) {
        val json = detail.rawLinearActivity
        if (json == null) emptyMap<String, String>() else groupRawActivityByUser(json)
    }
    val githubByUser = remember(detail.rawGithubActivity) {
        val root = (detail.rawGithubActivity as? JsonObject)?.get("byUser") as? JsonObject
        root?.entries?.mapNotNull { entry ->
            (entry.value as? JsonObject)?.let { entry.key to it }
        }?.toMap().orEmpty()
    }

    if (linearGrouped.isEmpty() && githubByUser.isEmpty()) {
        val expired = RetentionCopy.isRawActivityExpired(detail.scheduledFor, Clock.System.now(), retentionDays)
        EmptyState(
            title = if (expired) "Raw activity removed" else "No raw activity captured",
            subtitle = if (expired) {
                RetentionCopy.rawActivityExpiredSubtitle(retentionDays)
            } else {
                "This run pulled no Linear or GitHub signals — once members log work, their activity feeds will appear here."
            },
            icon = { LucideList(size = 26) },
        )
        return
    }

    Column(modifier = Modifier.fillMaxWidth()) {
        if (linearGrouped.isNotEmpty()) {
            SectionEyebrow("Linear")
            linearGrouped.forEach { (user, json) -> RawActivityGroup(user, json) }
        }
        if (githubByUser.isNotEmpty()) {
            Box(modifier = Modifier.margin(top = if (linearGrouped.isNotEmpty()) Spacing.lg else Spacing.none)) {
                SectionEyebrow("GitHub")
            }
            githubByUser.values.forEach { obj -> GithubActivityGroup(obj) }
        }
    }
}

@Composable
private fun SectionEyebrow(label: String) {
    SpanText(
        text = label.uppercase(),
        modifier = Modifier
            .fontSize(Typography.caption.size)
            .fontWeight(600)
            .color(Palette.textTertiary)
            .margin(bottom = Spacing.sm)
            .styleModifier {
                property("text-transform", "none")
                property("letter-spacing", "0.06em")
            },
    )
}

@Composable
private fun GithubActivityGroup(obj: JsonObject) {
    val displayName = obj["displayName"]?.jsonPrimitive?.contentOrNull ?: "Unknown"
    val login = obj["login"]?.jsonPrimitive?.contentOrNull
    val commits = obj["commits"]?.jsonArray.orEmpty()
    val merged = obj["mergedPrs"]?.jsonArray.orEmpty()
    val open = obj["openPrs"]?.jsonArray.orEmpty()
    val reviews = obj["reviews"]?.jsonArray.orEmpty()

    val totalSignals = commits.size + merged.size + open.size + reviews.size
    if (totalSignals == 0) return

    var expanded by remember(displayName) { mutableStateOf(false) }
    Card(modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.sm), padded = false) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(Spacing.md)
                .cursor(Cursor.Pointer)
                .onClick { expanded = !expanded },
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Column {
                SpanText(
                    text = displayName,
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .fontWeight(600)
                        .color(Palette.textPrimary),
                )
                if (login != null) {
                    SpanText(
                        text = "@$login",
                        modifier = Modifier
                            .fontSize(Typography.caption.size)
                            .color(Palette.textTertiary)
                            .styleModifier { property("font-family", Typography.FONT_MONO) },
                    )
                }
            }
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.gap(Spacing.sm),
            ) {
                if (commits.isNotEmpty()) MetaCount(commits.size, "commits")
                if (merged.isNotEmpty()) MetaCount(merged.size, "merged")
                if (open.isNotEmpty()) MetaCount(open.size, "open")
                if (reviews.isNotEmpty()) MetaCount(reviews.size, "reviews")
                SpanText(
                    text = if (expanded) "Hide" else "Show",
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.accent),
                )
            }
        }
        if (expanded) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(left = Spacing.md, right = Spacing.md, bottom = Spacing.md),
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth().gap(Spacing.md),
                ) {
                    if (commits.isNotEmpty()) GhSubSection("Commits") {
                        commits.forEach { CommitRow(it.jsonObject) }
                    }
                    if (merged.isNotEmpty()) GhSubSection("Merged PRs") {
                        merged.forEach { PrRow(it.jsonObject, merged = true) }
                    }
                    if (open.isNotEmpty()) GhSubSection("Open PRs") {
                        open.forEach { PrRow(it.jsonObject, merged = false) }
                    }
                    if (reviews.isNotEmpty()) GhSubSection("Reviews given") {
                        reviews.forEach { ReviewRow(it.jsonObject) }
                    }
                }
            }
        }
    }
}

@Composable
private fun MetaCount(n: Int, label: String) {
    SpanText(
        text = "$n $label",
        modifier = Modifier
            .fontSize(Typography.caption.size)
            .color(Palette.textSecondary)
            .styleModifier { property("font-variant-numeric", "tabular-nums") },
    )
}

@Composable
private fun GhSubSection(label: String, content: @Composable () -> Unit) {
    Column(
        modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "6px") },
    ) {
        SpanText(
            text = label.uppercase(),
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(600)
                .color(Palette.textTertiary)
                .styleModifier {
                    property("text-transform", "none")
                    property("letter-spacing", "0.06em")
                },
        )
        content()
    }
}

@Composable
private fun CommitRow(obj: JsonObject) {
    val sha = obj["shortSha"]?.jsonPrimitive?.contentOrNull
        ?: obj["sha"]?.jsonPrimitive?.contentOrNull?.take(7)
        ?: "—"
    val repo = obj["repo"]?.jsonPrimitive?.contentOrNull ?: ""
    val message = obj["message"]?.jsonPrimitive?.contentOrNull ?: ""
    val url = obj["url"]?.jsonPrimitive?.contentOrNull
    val authoredAt = obj["authoredAt"]?.jsonPrimitive?.contentOrNull
        ?.let { runCatching { Instant.parse(it) }.getOrNull() }
    Row(
        verticalAlignment = Alignment.Top,
        modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "10px") },
    ) {
        SpanText(
            text = sha,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(500)
                .color(Palette.accent)
                .styleModifier {
                    property("font-family", Typography.FONT_MONO)
                    property("min-width", "60px")
                },
        )
        Column(
            modifier = Modifier.styleModifier {
                property("flex", "1")
                property("min-width", "0")
            },
        ) {
            if (url != null) {
                A(
                    href = url,
                    attrs = Modifier
                        .color(Palette.textPrimary)
                        .fontSize(Typography.bodySmall.size)
                        .styleModifier { property("text-decoration", "none") }
                        .toAttrs { target(ATarget.Blank) },
                ) { SpanText(message) }
            } else {
                SpanText(
                    message,
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(Palette.textPrimary),
                )
            }
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier
                    .gap(Spacing.xs)
                    .styleModifier { property("flex-wrap", "wrap") },
            ) {
                SpanText(
                    text = repo,
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textTertiary),
                )
                if (authoredAt != null) {
                    SpanText(
                        text = "·",
                        modifier = Modifier
                            .fontSize(Typography.caption.size)
                            .color(Palette.textTertiary),
                    )
                    SpanText(
                        text = formatCommitTime(authoredAt),
                        modifier = Modifier
                            .fontSize(Typography.caption.size)
                            .color(Palette.textTertiary)
                            .styleModifier { property("font-variant-numeric", "tabular-nums") },
                    )
                }
            }
        }
    }
}

private fun formatCommitTime(instant: Instant): String {
    val now = Clock.System.now()
    val relative = RelativeTime.format(instant, now)
    val tz = TimeZone.currentSystemDefault()
    val ldt = instant.toLocalDateTime(tz)
    val month = ldt.month.name.lowercase().replaceFirstChar { it.uppercase() }.take(3)
    val time = "${ldt.hour.toString().padStart(2, '0')}:${ldt.minute.toString().padStart(2, '0')}"
    return "$relative — $month ${ldt.dayOfMonth}, $time"
}

@Composable
private fun PrRow(obj: JsonObject, merged: Boolean) {
    val repo = obj["repo"]?.jsonPrimitive?.contentOrNull ?: ""
    val number = obj["number"]?.jsonPrimitive?.intOrNull
    val title = obj["title"]?.jsonPrimitive?.contentOrNull ?: ""
    val url = obj["url"]?.jsonPrimitive?.contentOrNull
    val isDraft = obj["isDraft"]?.jsonPrimitive?.booleanOrNull == true
    val handle = if (number != null) "$repo#$number" else repo
    Row(
        verticalAlignment = Alignment.Top,
        modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "10px") },
    ) {
        SpanText(
            text = handle,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(500)
                .color(Palette.accent)
                .styleModifier {
                    property("font-family", Typography.FONT_MONO)
                    property("min-width", "180px")
                },
        )
        Column(
            modifier = Modifier.styleModifier {
                property("flex", "1")
                property("min-width", "0")
            },
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.styleModifier {
                    property("gap", "6px")
                    property("flex-wrap", "wrap")
                },
            ) {
                if (url != null) {
                    A(
                        href = url,
                        attrs = Modifier
                            .color(Palette.textPrimary)
                            .fontSize(Typography.bodySmall.size)
                            .styleModifier { property("text-decoration", "none") }
                            .toAttrs { target(ATarget.Blank) },
                    ) { SpanText(title) }
                } else {
                    SpanText(
                        title,
                        modifier = Modifier
                            .fontSize(Typography.bodySmall.size)
                            .color(Palette.textPrimary),
                    )
                }
                if (isDraft) Badge(label = "draft", tone = BadgeTone.Neutral)
            }
        }
    }
}

@Composable
private fun ReviewRow(obj: JsonObject) {
    val repo = obj["repo"]?.jsonPrimitive?.contentOrNull ?: ""
    val prNumber = obj["prNumber"]?.jsonPrimitive?.intOrNull
    val state = obj["state"]?.jsonPrimitive?.contentOrNull?.lowercase() ?: ""
    val handle = if (prNumber != null) "$repo#$prNumber" else repo
    val (badgeLabel, tone) = when (state) {
        "approved" -> "approved" to BadgeTone.Success
        "changes_requested" -> "changes" to BadgeTone.Warning
        "commented" -> "commented" to BadgeTone.Neutral
        "dismissed" -> "dismissed" to BadgeTone.Neutral
        else -> state to BadgeTone.Neutral
    }
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "10px") },
    ) {
        SpanText(
            text = handle,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(500)
                .color(Palette.accent)
                .styleModifier {
                    property("font-family", Typography.FONT_MONO)
                    property("min-width", "180px")
                },
        )
        Badge(label = badgeLabel, tone = tone)
    }
}

@Composable
private fun RawActivityGroup(user: String, json: String) {
    var expanded by remember(user) { mutableStateOf(false) }
    Card(modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.sm), padded = false) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(Spacing.md)
                .cursor(Cursor.Pointer)
                .onClick { expanded = !expanded },
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            SpanText(
                text = user,
                modifier = Modifier
                    .fontSize(Typography.body.size)
                    .fontWeight(600)
                    .color(Palette.textPrimary),
            )
            SpanText(
                text = if (expanded) "Hide" else "Show",
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.accent),
            )
        }
        if (expanded) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(left = Spacing.md, right = Spacing.md, bottom = Spacing.md)
                    .styleModifier {
                        property("font-family", Typography.FONT_MONO)
                        property("font-size", "12px")
                        property("white-space", "pre-wrap")
                        property("overflow-wrap", "anywhere")
                        property("color", "rgb(9, 9, 11)")
                    },
            ) {
                SpanText(text = json)
            }
        }
    }
}

private fun groupRawActivityByUser(json: kotlinx.serialization.json.JsonElement): Map<String, String> {
    val pretty = Json { prettyPrint = true; ignoreUnknownKeys = true }
    val arr = (json as? kotlinx.serialization.json.JsonArray)
    val obj = (json as? kotlinx.serialization.json.JsonObject)

    return when {
        obj != null -> {
            obj.entries.associate { (k, v) -> k to pretty.encodeToString(kotlinx.serialization.json.JsonElement.serializer(), v) }
        }
        arr != null -> {
            val grouped = mutableMapOf<String, MutableList<kotlinx.serialization.json.JsonElement>>()
            arr.forEach { el ->
                val elObj = el as? kotlinx.serialization.json.JsonObject ?: return@forEach
                val user = elObj["userId"]?.let { (it as? kotlinx.serialization.json.JsonPrimitive)?.content }
                    ?: elObj["assigneeId"]?.let { (it as? kotlinx.serialization.json.JsonPrimitive)?.content }
                    ?: elObj["user"]?.let { (it as? kotlinx.serialization.json.JsonPrimitive)?.content }
                    ?: "unknown"
                grouped.getOrPut(user) { mutableListOf() }.add(el)
            }
            grouped.mapValues { (_, list) ->
                pretty.encodeToString(
                    kotlinx.serialization.json.JsonArray.serializer(),
                    kotlinx.serialization.json.JsonArray(list),
                )
            }
        }
        else -> mapOf("activity" to pretty.encodeToString(kotlinx.serialization.json.JsonElement.serializer(), json))
    }
}
